import type {
  EmailFailuresResponse,
  EmailFailuresFilters,
  BulkArchiveRequest,
  ArchiveByInvoiceRequest,
  ArchiveResponse,
} from "@/types/EmailFailure";
import type { ApiResponse } from "@/types/api";
import { DateTime } from "luxon";
/**
 * URLs pour les endpoints d'échecs d'email
 */
export const emailFailuresUrl = {
  list: "/email-failures",
  archive: "/email-failures/{id}/archive",
  unarchive: "/email-failures/{id}/unarchive",
  archiveBulk: "/email-failures/archive-bulk",
  archiveByInvoice: "/email-failures/archive-by-invoice",
};

/**
 * Composable pour gérer les échecs de livraison d'emails
 */
export const useEmailFailures = () => {
  const pdfClient = usePdfSanctumClient();

  return {
    /**
     * Récupère la liste des échecs d'email avec filtres
     */
    getEmailFailures: async (
      filters: EmailFailuresFilters = {}
    ): Promise<ApiResponse<EmailFailuresResponse>> => {
      try {
        const queryParams = new URLSearchParams();
        const start_date = DateTime.fromJSDate(filters.start_date).toISODate();
        const end_date = DateTime.fromJSDate(filters.end_date).toISODate();

        if (filters.show_archived !== undefined) {
          queryParams.append('show_archived', filters.show_archived.toString());
        }
        if (filters.start_date) {
          queryParams.append('start_date', start_date);
        }
        if (filters.end_date) {
          queryParams.append('end_date', end_date);
        }
        if (filters.reason) {
          queryParams.append('reason', filters.reason);
        }
        if (filters.tenant) {
          queryParams.append('tenant', filters.tenant);
        }
        if (filters.no_pagination !== undefined) {
          queryParams.append('no_pagination', filters.no_pagination.toString());
        }

        const url = queryParams.toString()
          ? `${emailFailuresUrl.list}?${queryParams.toString()}`
          : emailFailuresUrl.list;

        const response = await pdfClient(url, {
          method: "GET",
        });

        return {
          status: 200,
          data: response as EmailFailuresResponse,
        };
      } catch (error: any) {
        console.error("Failed to fetch email failures:", error);
        const formattedError = useApiError(error);

        return {
          status: formattedError.code || 500,
          error: formattedError.message,
          isValidationError: formattedError.isValidationError,
          bag: formattedError.bag,
        };
      }
    },

    /**
     * Archive un échec d'email
     */
    archiveEmailFailure: async (
      id: number
    ): Promise<ApiResponse<ArchiveResponse>> => {
      try {
        const url = buildUrl(emailFailuresUrl.archive, { id });

        const response = await pdfClient(url, {
          method: "POST",
        });

        return {
          status: 200,
          data: response as ArchiveResponse,
        };
      } catch (error: any) {
        console.error("Failed to archive email failure:", error);
        const formattedError = useApiError(error);

        return {
          status: formattedError.code || 500,
          error: formattedError.message,
          isValidationError: formattedError.isValidationError,
          bag: formattedError.bag,
        };
      }
    },

    /**
     * Désarchive un échec d'email
     */
    unarchiveEmailFailure: async (
      id: number
    ): Promise<ApiResponse<ArchiveResponse>> => {
      try {
        const url = buildUrl(emailFailuresUrl.unarchive, { id });

        const response = await pdfClient(url, {
          method: "POST",
        });

        return {
          status: 200,
          data: response as ArchiveResponse,
        };
      } catch (error: any) {
        console.error("Failed to unarchive email failure:", error);
        const formattedError = useApiError(error);

        return {
          status: formattedError.code || 500,
          error: formattedError.message,
          isValidationError: formattedError.isValidationError,
          bag: formattedError.bag,
        };
      }
    },

    /**
     * Archive plusieurs échecs d'email en masse
     */
    archiveBulk: async (
      request: BulkArchiveRequest
    ): Promise<ApiResponse<ArchiveResponse>> => {
      try {
        const response = await pdfClient(emailFailuresUrl.archiveBulk, {
          method: "POST",
          body: request,
        });

        return {
          status: 200,
          data: response as ArchiveResponse,
        };
      } catch (error: any) {
        console.error("Failed to bulk archive email failures:", error);
        const formattedError = useApiError(error);

        return {
          status: formattedError.code || 500,
          error: formattedError.message,
          isValidationError: formattedError.isValidationError,
          bag: formattedError.bag,
        };
      }
    },

    /**
     * Archive automatiquement tous les échecs associés à une facture
     * Utilisé après renvoi réussi ou mise à jour d'email
     */
    archiveByInvoice: async (
      request: ArchiveByInvoiceRequest
    ): Promise<ApiResponse<ArchiveResponse>> => {
      try {
        const response = await pdfClient(emailFailuresUrl.archiveByInvoice, {
          method: "POST",
          body: request,
        });

        return {
          status: 200,
          data: response as ArchiveResponse,
        };
      } catch (error: any) {
        console.error("Failed to archive by invoice:", error);
        const formattedError = useApiError(error);

        return {
          status: formattedError.code || 500,
          error: formattedError.message,
          isValidationError: formattedError.isValidationError,
          bag: formattedError.bag,
        };
      }
    },
  };
};
